import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const APITester = () => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  const API_BASE = 'https://predictive-maintenance-v2-production.up.railway.app/api';

  const testCategories = {
    auth: {
      name: 'Authentication',
      endpoints: [
        { name: 'Login Test', method: 'POST', url: '/auth/login', body: { email: 'sarah.johnson@company.com', password: 'ops123' } },
        { name: 'Auth Test', method: 'GET', url: '/auth/test' }
      ]
    },
    equipment: {
      name: 'Equipment',
      endpoints: [
        { name: 'Get All Equipment', method: 'GET', url: '/equipment' },
        { name: 'Get Equipment Stats', method: 'GET', url: '/equipment/stats' },
        { name: 'Get Equipment by Plant', method: 'GET', url: '/equipment?location=Milwaukee' }
      ]
    },
    alerts: {
      name: 'Alerts',
      endpoints: [
        { name: 'Get All Alerts', method: 'GET', url: '/alerts' },
        { name: 'Get Alert Stats', method: 'GET', url: '/alerts/stats' },
        { name: 'Get Critical Alerts', method: 'GET', url: '/alerts?severity=critical' }
      ]
    },
    tickets: {
      name: 'Maintenance Tickets',
      endpoints: [
        { name: 'Get All Tickets', method: 'GET', url: '/tickets' },
        { name: 'Get Open Tickets', method: 'GET', url: '/tickets?status=open' },
        { name: 'Get High Priority Tickets', method: 'GET', url: '/tickets?priority=high' }
      ]
    },
    vendors: {
      name: 'Vendors',
      endpoints: [
        { name: 'Get All Vendors', method: 'GET', url: '/vendors' },
        { name: 'Get Equipment Suppliers', method: 'GET', url: '/vendors?type=equipment_supplier' },
        { name: 'Get Service Providers', method: 'GET', url: '/vendors?type=service_provider' }
      ]
    },
    parts: {
      name: 'Parts',
      endpoints: [
        { name: 'Get All Parts', method: 'GET', url: '/parts' },
        { name: 'Get Low Stock Parts', method: 'GET', url: '/parts/inventory/low-stock' },
        { name: 'Get Mechanical Parts', method: 'GET', url: '/parts?category=mechanical' }
      ]
    },
    predictions: {
      name: 'Predictions',
      endpoints: [
        { name: 'Get Recent Predictions', method: 'GET', url: '/streaming/predictions/recent' },
        { name: 'Get High Confidence Predictions', method: 'GET', url: '/streaming/predictions/recent?confidence=0.8' }
      ]
    },
    streaming: {
      name: 'Streaming & Data',
      endpoints: [
        { name: 'Get Streaming Status', method: 'GET', url: '/streaming/status' },
        { name: 'Get Recent Alerts', method: 'GET', url: '/streaming/alerts/recent' },
        { name: 'Get Equipment History', method: 'GET', url: '/streaming/equipment/1/history?hours=24' }
      ]
    },
    maintenance: {
      name: 'Maintenance',
      endpoints: [
        { name: 'Get Cleanup Status', method: 'GET', url: '/maintenance/cleanup/status' },
        { name: 'Get System Health', method: 'GET', url: '/maintenance/system/health' }
      ]
    },
    chat: {
      name: 'Chat',
      endpoints: [
        { name: 'Get Sample Questions', method: 'GET', url: '/chat/questions' }
      ]
    }
  };

  const runTest = async (category, endpoint) => {
    const testKey = `${category}-${endpoint.name}`;
    setLoading(prev => ({ ...prev, [testKey]: true }));

    try {
      const options = {
        method: endpoint.method,
        headers: {
          'Content-Type': 'application/json',
        }
      };

      if (endpoint.body) {
        options.body = JSON.stringify(endpoint.body);
      }

      const response = await fetch(`${API_BASE}${endpoint.url}`, options);
      const data = await response.json();

      setResults(prev => ({
        ...prev,
        [testKey]: {
          success: response.ok,
          status: response.status,
          data: data,
          timestamp: new Date().toISOString()
        }
      }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [testKey]: {
          success: false,
          error: error.message,
          timestamp: new Date().toISOString()
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [testKey]: false }));
    }
  };

  const runAllTests = async () => {
    const categoriesToTest = selectedCategory === 'all' 
      ? Object.keys(testCategories) 
      : [selectedCategory];

    for (const category of categoriesToTest) {
      for (const endpoint of testCategories[category].endpoints) {
        await runTest(category, endpoint);
        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  };

  const getStatusColor = (result) => {
    if (!result) return 'text-gray-500';
    if (result.success) return 'text-green-600';
    return 'text-red-600';
  };

  const getStatusIcon = (result) => {
    if (!result) return '⏳';
    if (result.success) return '✅';
    return '❌';
  };

  const formatData = (data) => {
    if (typeof data === 'object') {
      return JSON.stringify(data, null, 2);
    }
    return String(data);
  };

  return (
    <div className={`min-h-screen p-6 ${themeClasses.bg.primary}`}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4 text-procogia-green">API Testing Dashboard</h1>
          <p className="text-lg mb-6">
            Test all backend API endpoints before deployment to ensure everything is working correctly.
          </p>

          <div className="flex flex-wrap gap-4 mb-6">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`px-4 py-2 rounded-lg border ${themeClasses.bg.secondary} ${themeClasses.border.primary} ${themeClasses.text.primary}`}
            >
              <option value="all">All Categories</option>
              {Object.entries(testCategories).map(([key, category]) => (
                <option key={key} value={key}>{category.name}</option>
              ))}
            </select>

            <button
              onClick={runAllTests}
              className="px-6 py-2 bg-procogia-green text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Run Tests
            </button>

            <button
              onClick={() => setResults({})}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear Results
            </button>
          </div>
        </div>

        <div className="grid gap-6">
          {Object.entries(testCategories).map(([categoryKey, category]) => {
            if (selectedCategory !== 'all' && selectedCategory !== categoryKey) return null;

            return (
              <div
                key={categoryKey}
                className={`rounded-lg border ${themeClasses.bg.card} p-6`}
              >
                <h2 className="text-xl font-semibold mb-4 text-procogia-blue">
                  {category.name}
                </h2>

                <div className="space-y-4">
                  {category.endpoints.map((endpoint, index) => {
                    const testKey = `${categoryKey}-${endpoint.name}`;
                    const result = results[testKey];
                    const isLoading = loading[testKey];

                    return (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 ${themeClasses.bg.accent} ${themeClasses.border.primary}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">
                              {isLoading ? '⏳' : getStatusIcon(result)}
                            </span>
                            <h3 className="font-medium">{endpoint.name}</h3>
                            <span className={`px-2 py-1 rounded text-xs font-mono ${
                              endpoint.method === 'GET' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {endpoint.method}
                            </span>
                          </div>

                            <button
                              onClick={() => runTest(categoryKey, endpoint)}
                              disabled={isLoading}
                              className="px-3 py-1 bg-procogia-blue text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                            >
                              {isLoading ? 'Testing...' : 'Test'}
                            </button>
                        </div>

                        <div className="mb-2">
                          <code className="text-sm text-gray-600 dark:text-gray-400">
                            {endpoint.method} {API_BASE}{endpoint.url}
                          </code>
                        </div>

                        {result && (
                          <div className="mt-3">
                            <div className="flex items-center gap-4 mb-2">
                              <span className={`font-medium ${getStatusColor(result)}`}>
                                Status: {result.status || 'Error'}
                              </span>
                              <span className="text-sm text-gray-500">
                                {new Date(result.timestamp).toLocaleTimeString()}
                              </span>
                            </div>

                            {result.success && result.data && (
                              <div>
                                <h4 className="font-medium mb-2">Response Data:</h4>
                                <pre className={`text-xs p-3 rounded overflow-x-auto max-h-40 ${themeClasses.bg.accent} ${themeClasses.text.secondary}`}>
                                  {formatData(result.data).slice(0, 1000)}
                                  {formatData(result.data).length > 1000 && '...'}
                                </pre>
                              </div>
                            )}

                            {result.error && (
                              <div>
                                <h4 className="font-medium mb-2 text-red-600">Error:</h4>
                                <pre className="text-xs bg-red-50 text-red-700 p-3 rounded">
                                  {result.error}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
          <h3 className="font-semibold mb-2">Testing Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {Object.values(results).filter(r => r.success).length}
              </div>
              <div className="text-sm">Passed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {Object.values(results).filter(r => !r.success).length}
              </div>
              <div className="text-sm">Failed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {Object.values(results).length}
              </div>
              <div className="text-sm">Total Tested</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APITester; 